import { useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useI18n } from "../i18n/I18nProvider";

export type PlacePickerKind = "hotel" | "restaurant" | "place" | "city";

export interface PickedPlace {
  name: string;
  types?: string[];
  city: string;
  address: string;
  latitude: number;
  longitude: number;
  source?: "google" | "nominatim" | "photon";
  placeId?: string;
  rating?: number;
  ratingCount?: number;
  country?: string;
  countryCode?: string;
}

export interface GooglePlacePhotoChoice {
  url: string;
  attributionName: string;
  attributionUrl?: string;
}

interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  addresstype?: string;
  type?: string;
  name?: string;
  address?: Record<string, string>;
}

interface PhotonFeature {
  geometry?: { coordinates?: [number, number] };
  properties?: Record<string, unknown> & {
    name?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    country?: string;
    countrycode?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
  };
}

interface SearchResult {
  id: string;
  name: string;
  description: string;
  city?: string;
  picked: PickedPlace;
}

interface GeocodedArea {
  place: PickedPlace;
  types: string[];
}

type GoogleWindow = Window & {
  google?: any;
  __tripAIGoogleMapsPromise?: Promise<any>;
  __mundelyGoogleMapsReady?: () => void;
};

const GOOGLE_MAPS_API_KEY = String(import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "").trim();
const USE_GOOGLE_PICKER = Boolean(GOOGLE_MAPS_API_KEY) && !Capacitor.isNativePlatform();

function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function resolveCity(address?: Record<string, string>): string {
  if (!address) return "";
  return address.city || address.town || address.village || address.municipality || address.county || "";
}

function resultToPlace(result: NominatimResult): PickedPlace {
  const first = result.display_name.split(",")[0]?.trim() || "";
  return {
    name: result.name?.trim() || first,
    city: resolveCity(result.address),
    country: String(result.address?.country || "").trim() || undefined,
    countryCode: String(result.address?.country_code || "").trim().toUpperCase() || undefined,
    address: result.display_name,
    latitude: Number(result.lat),
    longitude: Number(result.lon),
    types: [result.addresstype || "", result.type || ""],
    source: "nominatim"
  };
}

function photonToPlace(feature: PhotonFeature): PickedPlace | null {
  const coords = feature.geometry?.coordinates;
  const props = feature.properties || {};
  if (!coords || !Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return null;
  const name = String(props.name || "").trim();
  if (!name) return null;
  const city = String(props.city || props.town || props.village || props.county || "").trim();
  const street = [props.street, props.housenumber].filter(Boolean).join(" ").trim();
  const locality = [props.postcode, city, props.state, props.country].filter(Boolean).join(", ");
  return {
    name,
    city,
    country: String(props.country || "").trim() || undefined,
    countryCode: String(props.countrycode || "").trim().toUpperCase() || undefined,
    address: [street, locality].filter(Boolean).join(", ") || name,
    latitude: Number(coords[1]),
    longitude: Number(coords[0]),
    types: [String(props.osm_key || ""), String(props.osm_value || ""), String(props.type || "")],
    source: "photon"
  };
}

function dedupe(items: PickedPlace[]): PickedPlace[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = `${item.name.toLowerCase()}|${item.city.toLowerCase()}|${item.latitude.toFixed(4)}|${item.longitude.toFixed(4)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

async function searchNominatim(term: string, language: string): Promise<PickedPlace[]> {
  const params = new URLSearchParams({
    q: term,
    format: "jsonv2",
    addressdetails: "1",
    limit: "8",
    "accept-language": language
  });
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
    headers: { Accept: "application/json" }
  });
  if (!response.ok) throw new Error(`nominatim/${response.status}`);
  const data = (await response.json()) as NominatimResult[];
  return data.map(resultToPlace).filter((item) => item.name && Number.isFinite(item.latitude) && Number.isFinite(item.longitude));
}

async function searchPhoton(term: string, language: string): Promise<PickedPlace[]> {
  const params = new URLSearchParams({ q: term, limit: "8", lang: ["en","de","fr"].includes(language) ? language : "en" });
  const response = await fetch(`https://photon.komoot.io/api/?${params.toString()}`, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`photon/${response.status}`);
  const data = await response.json() as { features?: PhotonFeature[] };
  return (data.features || []).map(photonToPlace).filter((item): item is PickedPlace => Boolean(item));
}

async function resilientFallbackSearch(term: string, destination: string | undefined, language: string): Promise<PickedPlace[]> {
  const scoped = destination?.trim() ? `${term}, ${destination.trim()}` : term;
  const attempts: Array<() => Promise<PickedPlace[]>> = [
    () => searchNominatim(scoped, language),
    ...(scoped !== term ? [() => searchNominatim(term, language)] : []),
    () => searchPhoton(scoped, language),
    ...(scoped !== term ? [() => searchPhoton(term, language)] : [])
  ];
  const collected: PickedPlace[] = [];
  for (const attempt of attempts) {
    try {
      const found = await attempt();
      collected.push(...found);
      if (collected.length >= 6) break;
    } catch {
      // Secondary provider: keep trying the next source.
    }
  }
  return dedupe(collected).slice(0, 8);
}

/** City-only fallback on Android. Never show streets or POIs as a matching city. */
async function resilientCitySearch(term: string, language: string): Promise<PickedPlace[]> {
  const matches: PickedPlace[] = [];
  try {
    const params = new URLSearchParams({
      q: term, format: "jsonv2", addressdetails: "1",
      featuretype: "city", limit: "12", "accept-language": language
    });
    const response = await fetch("https://nominatim.openstreetmap.org/search?" + params, { headers: { Accept: "application/json" } });
    if (response.ok) {
      const rows = await response.json() as NominatimResult[];
      for (const row of rows) {
        const kind = String(row.addresstype || row.type || "").toLowerCase();
        if (!["city", "town", "village", "municipality", "administrative"].includes(kind)) continue;
        const picked = resultToPlace(row);
        if (picked.name && Number.isFinite(picked.latitude) && Number.isFinite(picked.longitude)) matches.push(picked);
      }
    }
  } catch { /* Fail over to Photon city layer. */ }
  if (!matches.some(place => isExactCityQuery(term, place))) {
    try {
      const params = new URLSearchParams({ q: term, lang: ["en","de","fr"].includes(language) ? language : "en", limit: "12", layer: "city" });
      const response = await fetch("https://photon.komoot.io/api/?" + params, { headers: { Accept: "application/json" } });
      if (response.ok) {
        const data = await response.json() as { features?: PhotonFeature[] };
        for (const feature of data.features || []) {
          const kind = String(feature.properties?.type || "").toLowerCase();
          if (kind && !["city", "town", "village", "municipality"].includes(kind)) continue;
          const picked = photonToPlace(feature);
          if (picked) matches.push(picked);
        }
      }
    } catch { /* An empty list is safer than an unrelated road. */ }
  }
  const query = normalizeText(term);
  return dedupe(matches)
    .filter(place => [place.name,place.city].map(normalizeText).some(name => name === query || name.startsWith(query)))
    .sort((a,b) => Number(isExactCityQuery(term,b))-Number(isExactCityQuery(term,a)))
    .slice(0,8);
}

export async function resolveCountryForPlaceQuery(term: string, language: string): Promise<{ countryCode: string; country?: string } | null> {
  const query = String(term || "").trim();
  if (!query) return null;
  const results = await resilientFallbackSearch(query, undefined, language);
  const match = results.find((item) => /^[A-Z]{2}$/.test(String(item.countryCode || "").toUpperCase()));
  if (!match?.countryCode) return null;
  return { countryCode: match.countryCode.toUpperCase(), country: match.country };
}

function googleWindow(): GoogleWindow {
  return window as GoogleWindow;
}

function isLoaderError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || "");
  return message.includes("google-maps/missing-key") || message.includes("google-maps/load-failed");
}

export async function loadGoogleMaps(): Promise<any> {
  if (!GOOGLE_MAPS_API_KEY) throw new Error("google-maps/missing-key");
  const target = googleWindow();
  if (target.google?.maps?.importLibrary) return target.google;
  if (target.__tripAIGoogleMapsPromise) return target.__tripAIGoogleMapsPromise;

  target.__tripAIGoogleMapsPromise = new Promise((resolve, reject) => {
    let settled = false;
    let interval = 0;
    let ownedScript: HTMLScriptElement | null = null;
    const cleanup = () => { window.clearTimeout(timeout); if (interval) window.clearInterval(interval); if (target.__mundelyGoogleMapsReady === ready) delete target.__mundelyGoogleMapsReady; };
    const ready = () => { if (settled || !target.google?.maps?.importLibrary) return; settled = true; cleanup(); resolve(target.google); };
    const fail = () => { if (settled) return; settled = true; cleanup(); ownedScript?.remove(); reject(new Error("google-maps/load-failed")); };
    const timeout = window.setTimeout(fail, 20000);
    target.__mundelyGoogleMapsReady = ready;
    const existing = document.querySelector<HTMLScriptElement>("script[data-tripai-google-maps]");
    if (existing) {
      existing.addEventListener("error", fail, { once: true });
      interval = window.setInterval(ready, 50);
      ready();
      return;
    }
    const script = document.createElement("script");
    ownedScript = script;
    script.dataset.tripaiGoogleMaps = "true";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&v=weekly&loading=async&libraries=places&callback=__mundelyGoogleMapsReady`;
    // With loading=async the load event may precede the Maps API readiness callback.
    script.onload = ready;
    script.onerror = fail;
    document.head.appendChild(script);
  }).catch((error) => {
    target.__tripAIGoogleMapsPromise = undefined;
    throw error;
  });

  return target.__tripAIGoogleMapsPromise;
}

function googleCity(addressComponents: any[]): string {
  const preferred = ["locality", "postal_town", "administrative_area_level_3", "administrative_area_level_2", "administrative_area_level_1"];
  for (const type of preferred) {
    const component = addressComponents.find((item) => Array.isArray(item?.types) && item.types.includes(type));
    const text = component?.longText || component?.long_name || component?.shortText || component?.short_name;
    if (text) return String(text);
  }
  return "";
}

function googleCountry(addressComponents: any[]): { country?: string; countryCode?: string } {
  const component = addressComponents.find((item) => Array.isArray(item?.types) && item.types.includes("country"));
  if (!component) return {};
  const country = String(component?.longText || component?.long_name || "").trim() || undefined;
  const countryCode = String(component?.shortText || component?.short_name || "").trim().toUpperCase() || undefined;
  return { country, countryCode };
}

function googleLocation(location: any): { latitude: number; longitude: number } | null {
  const latitude = typeof location?.lat === "function" ? location.lat() : Number(location?.lat);
  const longitude = typeof location?.lng === "function" ? location.lng() : Number(location?.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

function googlePlaceToPicked(place: any): PickedPlace | null {
  const coords = googleLocation(place?.location);
  if (!coords) return null;
  const displayName = typeof place?.displayName === "string" ? place.displayName : String(place?.displayName?.text || "");
  const address = String(place?.formattedAddress || displayName || "").trim();
  const name = displayName.trim() || address.split(",")[0]?.trim() || "";
  if (!name) return null;
  const addressComponents = Array.isArray(place?.addressComponents) ? place.addressComponents : [];
  const countryInfo = googleCountry(addressComponents);
  return {
    name,
    city: googleCity(addressComponents),
    ...countryInfo,
    address,
    latitude: coords.latitude,
    longitude: coords.longitude,
    types: googleRawTypes(place),
    source: "google",
    placeId: String(place?.id || "") || undefined,
    rating: Number.isFinite(Number(place?.rating)) ? Number(place.rating) : undefined,
    ratingCount: Number.isFinite(Number(place?.userRatingCount)) ? Number(place.userRatingCount) : undefined
  };
}


function googleLegacyPlaceToPicked(place: any): PickedPlace | null {
  const coords = googleLocation(place?.geometry?.location);
  if (!coords) return null;
  const name = String(place?.name || "").trim();
  if (!name) return null;
  const address = String(place?.formatted_address || place?.vicinity || name).trim();
  const addressComponents = Array.isArray(place?.address_components) ? place.address_components : [];
  const city = googleCity(addressComponents) || String(place?.vicinity || "").split(",")[0]?.trim() || "";
  const countryInfo = googleCountry(addressComponents);
  return {
    name,
    city,
    ...countryInfo,
    address,
    latitude: coords.latitude,
    longitude: coords.longitude,
    types: googleRawTypes(place),
    source: "google",
    placeId: String(place?.place_id || "") || undefined,
    rating: Number.isFinite(Number(place?.rating)) ? Number(place.rating) : undefined,
    ratingCount: Number.isFinite(Number(place?.user_ratings_total)) ? Number(place.user_ratings_total) : undefined
  };
}

async function fetchGooglePlaceById(placeId: string): Promise<{ picked: PickedPlace; raw: any } | null> {
  if (!placeId) return null;
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;
  if (!Place) return null;
  const raw = new Place({ id: placeId });
  await raw.fetchFields({
    fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType", "rating", "userRatingCount"]
  });
  const picked = googlePlaceToPicked(raw);
  return picked ? { picked, raw } : null;
}

const CITY_PLACE_TYPES = new Set([
  "locality",
  "postal_town",
  "administrative_area_level_3",
  "administrative_area_level_4"
]);

function googleRawTypes(place: any): string[] {
  const types = Array.isArray(place?.types) ? place.types.map((value: unknown) => String(value)) : [];
  const primary = String(place?.primaryType || "");
  return primary && !types.includes(primary) ? [primary, ...types] : types;
}

function isGoogleCityLikeRaw(place: any): boolean {
  return googleRawTypes(place).some((type) => CITY_PLACE_TYPES.has(type));
}

function isGoogleCityAreaTypes(types: string[]): boolean {
  return types.some((type) => CITY_PLACE_TYPES.has(String(type)));
}

function matchesKind(place: any, kind: PlacePickerKind): boolean {
  if (kind === "place") return true;
  if (kind === "city") return isGoogleCityLikeRaw(place);
  const types = googleRawTypes(place);
  const primary = String(place?.primaryType || "");
  if (kind === "restaurant") {
    const foodTypes = ["restaurant", "cafe", "bar", "bakery", "meal_takeaway"];
    return foodTypes.some((type) => types.includes(type) || primary === type || primary.endsWith(`_${type}`) || primary.endsWith("_restaurant"));
  }
  const lodgingTypes = ["lodging", "hotel", "resort_hotel", "hostel", "motel", "inn", "guest_house", "bed_and_breakfast", "extended_stay_hotel"];
  return lodgingTypes.some((type) => types.includes(type) || primary === type);
}

async function googleTextSearch(
  term: string,
  kind: PlacePickerKind,
  language: string,
  bias?: PickedPlace | null,
  minRating = 0
): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;
  if (!Place?.searchByText) throw new Error("google-maps/places-unavailable");

  const request: Record<string, unknown> = {
    textQuery: term,
    fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType", "rating", "userRatingCount"],
    maxResultCount: 20,
    language
  };
  if (kind === "restaurant") {
    request.includedType = "restaurant";
    request.useStrictTypeFiltering = false;
  } else if (kind === "hotel") {
    request.includedType = "hotel";
    request.useStrictTypeFiltering = false;
  }
  if (minRating > 0 && (kind === "restaurant" || kind === "hotel")) request.minRating = minRating;
  if (bias && Number.isFinite(bias.latitude) && Number.isFinite(bias.longitude)) {
    request.locationBias = { lat: bias.latitude, lng: bias.longitude };
  }

  const response = await Place.searchByText(request);
  const raw = Array.isArray(response?.places) ? response.places : [];
  return dedupe(raw.filter((place: any) => matchesKind(place, kind)).map(googlePlaceToPicked).filter((place: PickedPlace | null): place is PickedPlace => Boolean(place)));
}



async function googleCitySearch(term: string, language: string): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;

  if (Place?.searchByText) {
    try {
      const response = await Place.searchByText({
        textQuery: term,
        fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType"],
        includedType: "locality",
        useStrictTypeFiltering: true,
        maxResultCount: 20,
        language
      });
      const strictCities = dedupe(
        (Array.isArray(response?.places) ? response.places : [])
          .filter((place: any) => isGoogleCityLikeRaw(place))
          .map(googlePlaceToPicked)
          .filter((item: PickedPlace | null): item is PickedPlace => Boolean(item))
      );
      if (strictCities.length > 0) {
        return strictCities.sort((a, b) => {
          const aExact = isExactCityQuery(term, a) ? 2 : isCityQueryMatch(term, a) ? 1 : 0;
          const bExact = isExactCityQuery(term, b) ? 2 : isCityQueryMatch(term, b) ? 1 : 0;
          return bExact - aExact;
        });
      }
    } catch {
      // Some municipalities are not classified as locality; fall back to the city collection.
    }
  }

  const AutocompleteSuggestion = places?.AutocompleteSuggestion;
  const AutocompleteSessionToken = places?.AutocompleteSessionToken;
  if (!AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
    const fallbackArea = await googleGeocodeArea(term);
    return fallbackArea && isGoogleCityAreaTypes(fallbackArea.types) && isExactCityQuery(term, fallbackArea.place)
      ? [fallbackArea.place]
      : [];
  }

  const sessionToken = AutocompleteSessionToken ? new AutocompleteSessionToken() : undefined;
  const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
    input: term,
    includedPrimaryTypes: ["(cities)"],
    language,
    ...(sessionToken ? { sessionToken } : {})
  });

  const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
  const predictions = suggestions
    .map((suggestion: any) => suggestion?.placePrediction)
    .filter(Boolean)
    .slice(0, 8);

  const resolved = await Promise.all(predictions.map(async (prediction: any) => {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["id", "displayName", "formattedAddress", "location", "addressComponents", "types", "primaryType"]
      });
      if (!isGoogleCityLikeRaw(place)) return null;
      const picked = googlePlaceToPicked(place);
      if (!picked) return null;
      return {
        ...picked,
        name: picked.name || picked.city,
        city: picked.city || picked.name
      };
    } catch {
      return null;
    }
  }));

  return dedupe(resolved.filter((item): item is PickedPlace => Boolean(item)))
    .sort((a, b) => {
      const aExact = isExactCityQuery(term, a) ? 2 : isCityQueryMatch(term, a) ? 1 : 0;
      const bExact = isExactCityQuery(term, b) ? 2 : isCityQueryMatch(term, b) ? 1 : 0;
      return bExact - aExact;
    });
}

function isExactCityQuery(term: string, place: PickedPlace | undefined): boolean {
  if (!place) return false;
  const query = normalizeText(term);
  if (!query) return false;
  const candidates = [
    place.name,
    place.city,
    place.address.split(",")[0] || ""
  ].map(normalizeText).filter(Boolean);
  return candidates.some((candidate) => candidate === query);
}

function isCityQueryMatch(term: string, place: PickedPlace | undefined): boolean {
  if (!place) return false;
  const query = normalizeText(term);
  if (!query) return false;
  const candidates = [
    place.name,
    place.city,
    place.address.split(",")[0] || ""
  ].map(normalizeText).filter(Boolean);
  return candidates.some((candidate) => candidate === query || candidate.startsWith(query) || query.startsWith(candidate));
}

function rankPlacesForArea(items: PickedPlace[], term: string, area?: PickedPlace | null): PickedPlace[] {
  const query = normalizeText(term);
  const areaNames = area ? [area.name, area.city, area.address.split(",")[0] || ""].map(normalizeText).filter(Boolean) : [];
  const score = (place: PickedPlace) => {
    const city = normalizeText(place.city);
    const address = normalizeText(place.address);
    const firstAddress = normalizeText(place.address.split(",")[0] || "");
    let value = 0;
    if (query && (city === query || firstAddress === query)) value += 120;
    else if (query && (city.startsWith(query) || firstAddress.startsWith(query))) value += 100;
    else if (query && address.includes(query)) value += 60;
    if (areaNames.some((name) => name && (city === name || firstAddress === name))) value += 80;
    else if (areaNames.some((name) => name && address.includes(name))) value += 40;
    value += Math.round((place.rating || 0) * 4);
    value += Math.min(10, Math.log10((place.ratingCount || 0) + 1) * 3);
    if (area) value -= Math.min(30, distanceKm(area, place) / 2);
    return value;
  };
  return [...items].sort((a, b) => score(b) - score(a) || (b.rating || 0) - (a.rating || 0) || (b.ratingCount || 0) - (a.ratingCount || 0) || a.name.localeCompare(b.name));
}

function distanceKm(a: PickedPlace, b: PickedPlace): number {
  const toRad = (value: number) => value * Math.PI / 180;
  const earthRadius = 6371;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const haversine = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

export async function searchGooglePointsOfInterest(
  city: PickedPlace,
  language: string
): Promise<PickedPlace[]> {
  if (!GOOGLE_MAPS_API_KEY) return [];
  const cityLabel = city.address || city.name;
  const queries = language === "it"
    ? [`attrazioni turistiche a ${cityLabel}`, `musei monumenti a ${cityLabel}`]
    : language === "es"
      ? [`atracciones turísticas en ${cityLabel}`, `museos monumentos en ${cityLabel}`]
      : [`tourist attractions in ${cityLabel}`, `museums landmarks in ${cityLabel}`];

  const collected: PickedPlace[] = [];
  for (const query of queries) {
    try {
      const found = await googleTextSearch(query, "place", language, city);
      collected.push(...found);
    } catch {
      // Try the next query.
    }
  }

  return dedupe(collected)
    .filter((place) => normalizeText(place.name) !== normalizeText(city.name))
    .filter((place) => 40 >= distanceKm(city, place))
    .slice(0, 20);
}

async function googleNearbySearch(
  area: PickedPlace,
  kind: Exclude<PlacePickerKind, "place" | "city">,
  radius: number
): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  await google.maps.importLibrary("places");
  const PlacesService = google.maps?.places?.PlacesService;
  if (!PlacesService) throw new Error("google-maps/nearby-unavailable");

  const service = new PlacesService(document.createElement("div"));
  const type = kind === "restaurant" ? "restaurant" : "lodging";

  return await new Promise<PickedPlace[]>((resolve, reject) => {
    service.nearbySearch(
      {
        location: { lat: area.latitude, lng: area.longitude },
        radius,
        type
      },
      (raw: any[], status: string) => {
        const ok = google.maps.places.PlacesServiceStatus.OK;
        const zero = google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
        if (status === zero) {
          resolve([]);
          return;
        }
        if (status !== ok) {
          reject(new Error(`google-maps/nearby-${status}`));
          return;
        }
        const items = Array.isArray(raw) ? raw : [];
        resolve(
          dedupe(
            items
              .filter((place: any) => matchesKind(place, kind))
              .map(googleLegacyPlaceToPicked)
              .filter((place: PickedPlace | null): place is PickedPlace => Boolean(place))
          )
        );
      }
    );
  });
}

async function googleLegacyTextSearch(
  term: string,
  kind: Exclude<PlacePickerKind, "place" | "city">,
  bias?: PickedPlace | null
): Promise<PickedPlace[]> {
  const google = await loadGoogleMaps();
  await google.maps.importLibrary("places");
  const PlacesService = google.maps?.places?.PlacesService;
  if (!PlacesService) return [];

  const service = new PlacesService(document.createElement("div"));
  const request: Record<string, unknown> = { query: term };
  if (bias) {
    request.location = { lat: bias.latitude, lng: bias.longitude };
    request.radius = 25000;
  }

  return await new Promise<PickedPlace[]>((resolve, reject) => {
    service.textSearch(request, (raw: any[], status: string) => {
      const ok = google.maps.places.PlacesServiceStatus.OK;
      const zero = google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
      if (status === zero) {
        resolve([]);
        return;
      }
      if (status !== ok) {
        reject(new Error(`google-maps/text-${status}`));
        return;
      }
      const items = Array.isArray(raw) ? raw : [];
      resolve(
        dedupe(
          items
            .filter((place: any) => matchesKind(place, kind))
            .map(googleLegacyPlaceToPicked)
            .filter((place: PickedPlace | null): place is PickedPlace => Boolean(place))
        )
      );
    });
  });
}

async function googleGeocodeArea(term: string): Promise<GeocodedArea | null> {
  const google = await loadGoogleMaps();
  const geocoder = new google.maps.Geocoder();
  const response = await geocoder.geocode({ address: term });
  const results = Array.isArray(response?.results) ? response.results : [];
  const area = results.find((result: any) => {
    const types = Array.isArray(result?.types) ? result.types : [];
    return types.some((type: string) => ["locality", "postal_town", "administrative_area_level_4", "administrative_area_level_3", "administrative_area_level_2", "administrative_area_level_1", "sublocality", "neighborhood", "country"].includes(type));
  });
  if (!area) return null;
  const coords = googleLocation(area?.geometry?.location);
  if (!coords) return null;
  const place: PickedPlace = {
    name: String(area?.address_components?.[0]?.long_name || term).trim(),
    city: googleCity(Array.isArray(area?.address_components) ? area.address_components.map((component: any) => ({
      longText: component.long_name,
      shortText: component.short_name,
      types: component.types
    })) : []),
    address: String(area?.formatted_address || term).trim(),
    latitude: coords.latitude,
    longitude: coords.longitude,
    types: Array.isArray(area?.types) ? area.types.map(String) : [],
    source: "google",
    placeId: String(area?.place_id || "") || undefined
  };
  return { place, types: Array.isArray(area?.types) ? area.types.map(String) : [] };
}

function isAreaQuery(term: string, area: GeocodedArea | null): boolean {
  if (!area) return false;
  const query = normalizeText(term);
  if (!query) return false;
  const areaName = normalizeText(area.place.name);
  const address = normalizeText(area.place.address);
  const firstAddressPart = normalizeText(area.place.address.split(",")[0] || "");
  const queryTokens = query.split(" ").filter(Boolean);
  const addressContainsAllTokens = queryTokens.length > 0 && queryTokens.every((token) => address.includes(token));
  return query === areaName || query === firstAddressPart || addressContainsAllTokens;
}

async function searchActivitiesAroundArea(
  area: PickedPlace,
  kind: Exclude<PlacePickerKind, "place" | "city">,
  language: string,
  searchTerm: string
): Promise<PickedPlace[]> {
  let found: PickedPlace[] = [];
  const radii = [5000, 12000, 25000];
  for (const radius of radii) {
    try {
      found = dedupe([...found, ...(await googleNearbySearch(area, kind, radius))]);
    } catch {
      // Continue with wider searches and text search.
    }
    if (found.length >= 12) break;
  }

  const categoryQueries = kind === "restaurant"
    ? [`restaurants in ${area.address || area.name}`, `restaurants ${area.name}`]
    : [`hotels in ${area.address || area.name}`, `hotels ${area.name}`, `lodging in ${area.address || area.name}`];
  for (const query of categoryQueries) {
    try {
      const textFound = await googleTextSearch(query, kind, language, area);
      found = dedupe([...found, ...textFound]);
    } catch {
      // Try the next Google strategy.
    }
  }
  if (found.length < 8) {
    for (const query of categoryQueries) {
      try {
        const legacyFound = await googleLegacyTextSearch(query, kind, area);
        found = dedupe([...found, ...legacyFound]);
      } catch {
        // Return the results already collected.
      }
    }
  }
  return rankPlacesForArea(found, searchTerm, area).slice(0, 20);
}

export interface TripPhotoCandidate {
  placeId: string;
  photoIndex: number;
  name: string;
  address: string;
  url: string;
}

export async function fetchTripPhotoCandidates(destination: string, language: string, maxCount = 8): Promise<TripPhotoCandidate[]> {
  const term = destination.trim();
  if (!term || !GOOGLE_MAPS_API_KEY) return [];
  const candidates: TripPhotoCandidate[] = [];
  const seen = new Set<string>();

  const addPhotos = async (place: PickedPlace, count: number) => {
    if (!place.placeId || candidates.length >= maxCount) return;
    try {
      const photos = await fetchGooglePlacePhotos(place.placeId, count);
      photos.forEach((photo, photoIndex) => {
        const key = `${place.placeId}:${photoIndex}`;
        if (seen.has(key) || candidates.length >= maxCount) return;
        seen.add(key);
        candidates.push({
          placeId: place.placeId as string,
          photoIndex,
          name: place.name,
          address: place.address,
          url: photo.url
        });
      });
    } catch {
      // A destination can legitimately have no Google photos.
    }
  };

  let area: PickedPlace | null = null;
  try {
    area = (await googleGeocodeArea(term))?.place || null;
    if (area) await addPhotos(area, 2);
  } catch {
    area = null;
  }

  const queries = [`tourist attractions in ${term}`, `landmarks in ${term}`];
  for (const query of queries) {
    if (candidates.length >= maxCount) break;
    try {
      const places = await googleTextSearch(query, "place", language, area);
      for (const place of places.slice(0, 6)) {
        await addPhotos(place, 1);
        if (candidates.length >= maxCount) break;
      }
    } catch {
      // Try the next query.
    }
  }
  return candidates.slice(0, maxCount);
}

export async function fetchGooglePlacePhotos(placeId: string, maxCount = 6): Promise<GooglePlacePhotoChoice[]> {
  if (!placeId || !GOOGLE_MAPS_API_KEY) return [];
  const google = await loadGoogleMaps();
  const places = await google.maps.importLibrary("places");
  const Place = places?.Place;
  if (!Place) return [];
  const place = new Place({ id: placeId });
  await place.fetchFields({ fields: ["photos"] });
  const photos = Array.isArray(place.photos) ? place.photos.slice(0, maxCount) : [];
  return photos.flatMap((photo: any) => {
    const url = typeof photo?.getURI === "function" ? String(photo.getURI({ maxWidth: 1000, maxHeight: 700 }) || "") : "";
    if (!url) return [];
    const attribution = Array.isArray(photo?.authorAttributions) ? photo.authorAttributions[0] : undefined;
    return [{
      url,
      attributionName: String(attribution?.displayName || "").trim(),
      attributionUrl: String(attribution?.uri || "").trim() || undefined
    } satisfies GooglePlacePhotoChoice];
  });
}

function placeForKind(place: PickedPlace, kind: PlacePickerKind): PickedPlace {
  if (kind !== "city") return place;
  const cityPlace = { ...place };
  delete cityPlace.rating;
  delete cityPlace.ratingCount;
  return cityPlace;
}

function resultsFromPlaces(items: PickedPlace[], kind: PlacePickerKind): SearchResult[] {
  return items.map((rawPlace, index) => {
    const place = placeForKind(rawPlace, kind);
    return {
      id: place.placeId || `${place.source || "place"}-${index}-${place.latitude}-${place.longitude}`,
      name: place.name,
      city: place.city,
      description: place.address,
      picked: place
    };
  });
}

export function PlacePicker({
  destination,
  initialQuery,
  kind = "place",
  onPick,
  onClose,
  onManualEntry
}: {
  destination?: string;
  initialQuery?: string;
  kind?: PlacePickerKind;
  onPick: (place: PickedPlace) => void;
  onClose: () => void;
  onManualEntry?: () => void;
}) {
  const { t, language } = useI18n();
  const [query, setQuery] = useState(() => String(initialQuery || ""));
  const [rawResults, setResults] = useState<SearchResult[]>([]);
  const [minRating, setMinRating] = useState(0);
  const results = useMemo(() => rawResults.filter((result) => minRating === 0 || Math.max(minRating, result.picked.rating || 0) === (result.picked.rating || 0)), [rawResults, minRating]);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [selected, setSelected] = useState<PickedPlace | null>(null);
  const [mapCenter, setMapCenter] = useState<PickedPlace | null>(null);
  const [mapZoom, setMapZoom] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [googleLoadError, setGoogleLoadError] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const googleMapNodeRef = useRef<HTMLDivElement | null>(null);
  const googleMapRef = useRef<any>(null);
  const googleMarkerRefs = useRef<any[]>([]);
  const googleMapClickListenerRef = useRef<any>(null);

  useEffect(() => {
    setQuery(String(initialQuery || ""));
  }, [initialQuery]);

  useEffect(() => {
    let cancelled = false;
    const term = destination?.trim();
    if (!term) return;

    const centerDestination = async () => {
      if (USE_GOOGLE_PICKER) {
        try {
          const area = await googleGeocodeArea(term);
          if (!cancelled && area) {
            setMapCenter(area.place);
            setMapZoom(7);
            setGoogleLoadError(false);
            return;
          }
        } catch (nextError) {
          if (!cancelled && isLoaderError(nextError)) setGoogleLoadError(true);
        }
      }
      const fallback = await resilientFallbackSearch(term, undefined, language);
      if (!cancelled && fallback[0]) setMapCenter(fallback[0]);
    };

    void centerDestination();
    return () => { cancelled = true; };
  }, [destination, language]);

  const mapPlace = selected || mapCenter;
  const osmMapUrl = useMemo(() => {
    if (!mapPlace || !Number.isFinite(mapPlace.latitude) || !Number.isFinite(mapPlace.longitude)) return "";
    const d = mapZoom >= 16 ? 0.012 : mapZoom >= 13 ? 0.08 : 0.8;
    const left = mapPlace.longitude - d;
    const right = mapPlace.longitude + d;
    const top = mapPlace.latitude + d;
    const bottom = mapPlace.latitude - d;
    const marker = selected ? `&marker=${encodeURIComponent(`${selected.latitude},${selected.longitude}`)}` : "";
    return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(`${left},${bottom},${right},${top}`)}&layer=mapnik${marker}`;
  }, [mapPlace, mapZoom, selected]);

  useEffect(() => {
    if (!USE_GOOGLE_PICKER || !googleMapNodeRef.current || !mapPlace) return;
    let cancelled = false;
    void loadGoogleMaps().then(async (google) => {
      if (cancelled || !googleMapNodeRef.current) return;
      const center = { lat: mapPlace.latitude, lng: mapPlace.longitude };
      if (!googleMapRef.current) {
        const maps = await google.maps.importLibrary("maps");
        googleMapRef.current = new maps.Map(googleMapNodeRef.current, {
          center,
          zoom: mapZoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: true
        });
      }

      googleMapClickListenerRef.current?.remove?.();
      googleMapClickListenerRef.current = googleMapRef.current.addListener("click", (event: any) => {
        const placeId = String(event?.placeId || "").trim();
        if (!placeId) return;
        event?.stop?.();
        void fetchGooglePlaceById(placeId)
          .then((detail) => {
            if (!detail || !matchesKind(detail.raw, kind)) return;
            const result = resultsFromPlaces([detail.picked], kind)[0];
            if (result) selectAndUseResult(result);
          })
          .catch(() => undefined);
      });

      googleMarkerRefs.current.forEach((marker) => marker.setMap(null));
      googleMarkerRefs.current = [];

      const markerItems = results.length > 0 ? results : selected ? [{ id: "selected", picked: selected }] : [];
      const bounds = new google.maps.LatLngBounds();
      markerItems.forEach((item: any) => {
        const position = { lat: item.picked.latitude, lng: item.picked.longitude };
        const marker = new google.maps.Marker({ map: googleMapRef.current, position, title: item.picked.name });
        if (item.id !== "selected") {
          marker.addListener("click", () => {
            const result = results.find((candidate) => candidate.id === item.id);
            if (result) selectAndUseResult(result);
          });
        }
        googleMarkerRefs.current.push(marker);
        bounds.extend(position);
      });

      if (selected) {
        googleMapRef.current.setCenter(center);
        googleMapRef.current.setZoom(17);
      } else if (results.length > 1) {
        googleMapRef.current.fitBounds(bounds, 48);
      } else {
        googleMapRef.current.setCenter(center);
        googleMapRef.current.setZoom(mapZoom);
      }
      setGoogleReady(true);
      setGoogleLoadError(false);
    }).catch((nextError) => {
      if (isLoaderError(nextError)) setGoogleLoadError(true);
    });
    return () => {
      cancelled = true;
      googleMapClickListenerRef.current?.remove?.();
      googleMapClickListenerRef.current = null;
    };
  }, [kind, mapPlace, mapZoom, results, selected]);

  async function search() {
    const term = query.trim();
    if (!term) return;
    setLoading(true);
    setError(false);
    setSelected(null);
    setSelectedResultId(null);
    setResults([]);

    try {
      if (USE_GOOGLE_PICKER) {
        try {
          const cityMatches = await googleCitySearch(term, language);

          if (kind === "city") {
            if (cityMatches.length > 0) {
              setResults(resultsFromPlaces(cityMatches, kind));
              setMapCenter(cityMatches[0]);
              setMapZoom(cityMatches.length > 1 ? 7 : 10);
              setGoogleLoadError(false);
              return;
            }

            const area = await googleGeocodeArea(term);
            if (area && isGoogleCityAreaTypes(area.types) && isExactCityQuery(term, area.place)) {
              setResults(resultsFromPlaces([area.place], kind));
              setMapCenter(area.place);
              setMapZoom(10);
              setGoogleLoadError(false);
              return;
            }
          } else if (kind === "place") {
            const foundPlaces = await googleTextSearch(term, "place", language, null);
            if (foundPlaces.length > 0) {
              setResults(resultsFromPlaces(foundPlaces, kind));
              setMapCenter(foundPlaces[0]);
              setMapZoom(14);
              setGoogleLoadError(false);
              return;
            }

            const area = await googleGeocodeArea(term);
            if (area) {
              setResults(resultsFromPlaces([area.place], kind));
              setMapCenter(area.place);
              setMapZoom(12);
              setGoogleLoadError(false);
              return;
            }
          } else {
            const exactCity = cityMatches.find((candidate) => isExactCityQuery(term, candidate));
            const partialCity = cityMatches.find((candidate) => isCityQueryMatch(term, candidate));
            let searchArea = exactCity || partialCity || null;
            if (!searchArea) {
              try {
                const geocodedArea = await googleGeocodeArea(term);
                if (geocodedArea && (isAreaQuery(term, geocodedArea) || isCityQueryMatch(term, geocodedArea.place))) searchArea = geocodedArea.place;
                if (!searchArea) {
                  const cityGeocodedArea = await googleGeocodeArea(`${term} City`);
                  if (cityGeocodedArea && (isAreaQuery(term, cityGeocodedArea) || isCityQueryMatch(term, cityGeocodedArea.place))) searchArea = cityGeocodedArea.place;
                }
              } catch {
                searchArea = null;
              }
            }

            if (searchArea) {
              setMapCenter(searchArea);
              setMapZoom(13);
              const found = await searchActivitiesAroundArea(searchArea, kind, language, term);
              setResults(resultsFromPlaces(found, kind));
              setError(found.length === 0);
              setGoogleLoadError(false);
              return;
            }

            let found = await googleTextSearch(term, kind, language, null);

            if (found.length === 0) {
              found = await googleLegacyTextSearch(term, kind, null);
            }

            if (found.length === 0 && destination?.trim()) {
              const scopedTerm = `${term}, ${destination.trim()}`;
              found = await googleTextSearch(scopedTerm, kind, language, null);
              if (found.length === 0) {
                found = await googleLegacyTextSearch(scopedTerm, kind, null);
              }
            }

            if (found.length > 0) {
              found = rankPlacesForArea(found, term, null);
              setResults(resultsFromPlaces(found.slice(0, 20), kind));
              setMapCenter(found[0]);
              setMapZoom(15);
              setGoogleLoadError(false);
              return;
            }
          }

          // Google can be available in a desktop browser but unavailable inside an
          // Android WebView because of API-key/referrer restrictions. Do not stop
          // here: continue with the provider-independent fallback search below.
          setGoogleLoadError(false);
        } catch (nextError) {
          if (isLoaderError(nextError)) {
            setGoogleLoadError(true);
          } else {
            setGoogleLoadError(false);
          }
        }
      }

      const fallback = kind === "city"
        ? await resilientCitySearch(term, language)
        : await resilientFallbackSearch(term, destination, language);
      // Nominatim/Photon are also valid fallbacks for hotels and restaurants.
      // Previously those results were deliberately discarded, so Android showed
      // an empty result list whenever the Google JS provider was blocked.
      const safeFallback = fallback;
      setResults(resultsFromPlaces(safeFallback, kind));
      if (safeFallback[0]) {
        setMapCenter(safeFallback[0]);
        setMapZoom(kind === "city" ? 10 : 14);
      }
      setError(safeFallback.length === 0);
    } catch {
      setError(true);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  function selectAndUseResult(result: SearchResult) {
    const picked = placeForKind(result.picked, kind);
    setSelectedResultId(result.id);
    setError(false);
    setSelected(picked);
    setMapCenter(picked);
    setMapZoom(kind === "city" ? 10 : 17);
    onPick(picked);
  }

  function renderMapPreview() {
    if (USE_GOOGLE_PICKER && (!googleLoadError || googleReady)) {
      return <div className="place-picker-google-map" ref={googleMapNodeRef} aria-label={t("places.mapPreview")} />;
    }
    if (osmMapUrl) {
      return <iframe title={t("places.mapPreview")} src={osmMapUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />;
    }
    return <div className="place-picker-map-empty">{t("places.mapEmpty")}</div>;
  }

  return (
    <div className="place-picker-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className="place-picker-modal" role="dialog" aria-modal="true" aria-label={t("places.pickerTitle")}>
        <div className="place-picker-header">
          <div>
            <p className="eyebrow">{t("places.pickerEyebrow")}</p>
            <h3>{t("places.pickerTitle")}</h3>
            <p>{t("places.pickerBody")}</p>
          </div>
          <button className="standard-modal-close place-picker-close" type="button" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
        </div>

        <div className="place-picker-search-row">
          <input
            className="input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") void search(); }}
            placeholder={kind === "hotel" ? t("places.searchHotelPlaceholder") : kind === "restaurant" ? t("places.searchRestaurantPlaceholder") : kind === "city" ? t("places.searchCityPlaceholder") : t("places.searchPlaceholder")}
            autoFocus
          />
          {(kind === "hotel" || kind === "restaurant") && (
            <label className="place-picker-rating-filter">
              <span>{t("places.minRating")}</span>
              <select className="input" value={String(minRating)} onChange={(event) => setMinRating(Number(event.target.value) || 0)}>
                <option value="0">{t("places.anyRating")}</option>
                <option value="3">3+</option>
                <option value="3.5">3.5+</option>
                <option value="4">4+</option>
                <option value="4.5">4.5+</option>
              </select>
            </label>
          )}
          <button className="button primary" type="button" onClick={() => void search()} disabled={loading || !query.trim()}>
            {loading ? t("places.searching") : t("places.search")}
          </button>
        </div>

        {onManualEntry && <div className="place-picker-manual"><button className="button secondary" type="button" onClick={onManualEntry}>{t("itinerary.enterManually")}</button></div>}
        {error && <div className="settings-error">{t("places.searchNoResults")}</div>}
        {googleLoadError && !googleReady && <div className="place-picker-provider-note">{USE_GOOGLE_PICKER ? t("places.googleFallback") : t("places.googleNotConfigured")}</div>}

        <div className="place-picker-body">
          <div className="place-picker-results">
            {results.length === 0 && !loading ? (
              <div className="place-picker-empty">{t("places.searchHint")}</div>
            ) : results.map((result) => (
              <button
                key={result.id}
                type="button"
                className={selectedResultId === result.id ? "place-picker-result active" : "place-picker-result"}
                onClick={() => selectAndUseResult(result)}
              >
                <div className="place-picker-result-title">
                  <strong>{result.name}</strong>
                  {kind !== "city" && typeof result.picked.rating === "number" && (
                    <span className="place-rating-badge">★ {result.picked.rating.toFixed(1)}{result.picked.ratingCount ? ` · ${result.picked.ratingCount}` : ""}</span>
                  )}
                </div>
                {result.city && <span>{result.city}</span>}
                <small>{result.description}</small>
              </button>
            ))}
          </div>

          <div className="place-picker-map-panel">
            {renderMapPreview()}
            {selected && (
              <div className="place-picker-selected">
                <div><strong>{selected.name}</strong><small>{selected.address}</small></div>
                <button className="button primary" type="button" onClick={() => onPick(selected)}>{t("places.usePlace")}</button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// Recover photos for saved map points that predate Google place identifiers.
// Name agreement and a small distance are both required before using the photo.
export async function fetchNamedPlacePhoto(point: PickedPlace): Promise<GooglePlacePhotoChoice | null> {
  if (!Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return null;
  const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g," ").trim();
  const words = normalize(point.name).split(" ").filter(word=>word.length>=3 && !["hotel","restaurant","cafe","the"].includes(word));
  if (!words.length) return null;
  const candidates = await googleTextSearch([point.name,point.city].filter(Boolean).join(" "), "place", "en", point);
  const match = candidates.find(candidate => 0.6 > distanceKm(point,candidate) && words.every(word=>normalize(candidate.name).includes(word)));
  if (!match?.placeId) return null;
  return (await fetchGooglePlacePhotos(match.placeId,1))[0] || null;
}
